import { App, TierartEnum } from '@ps20-frontend/frontend-models';
import { TgvProduktArt, TgvProduktSnapshot } from '../models/angebot.model';
import { TgvTier, TiergruppeState } from '../models/tiergruppe.model';

/**
 * The TKV store starts with an empty product object (`produktlinien: []`), so the product only
 * counts as loaded once the master product delivered its Produktlinien.
 */
const hasProduktlinien = (child: ChildProdukt | undefined): boolean =>
  (child?.produkt?.produktlinien?.length ?? 0) > 0;

/** The part of a child state the offer page reads. */
interface ChildProdukt {
  produkt?: {
    produktlinien?: Array<{
      produktName?: string;
      kurztext?: string;
      selected?: boolean;
      bruttopreis?: number | null;
    }>;
  } | null;
  vertragsoptionen?: { zahlungsweise?: string };
}

export type ReadChildState = (app: App, angebotsId?: string) => unknown;

const linienOf = (child: ChildProdukt | undefined): TgvProduktSnapshot['linien'] =>
  (child?.produkt?.produktlinien ?? []).map(linie => ({
    name: String(linie.produktName ?? ''),
    kurztext: linie.kurztext ?? '',
    selected: Boolean(linie.selected),
    bruttopreis: linie.bruttopreis != null && linie.bruttopreis > 0 ? linie.bruttopreis : null,
  }));

const zahlweiseOf = (child: ChildProdukt | undefined): string =>
  child?.vertragsoptionen?.zahlungsweise?.toLowerCase() ?? '';

const displayName = (tier: TgvTier, index: number): string =>
  tier.name.trim() === '' ? `Tier ${index + 1}` : tier.name.trim();

const thhSnapshot = (
  art: TgvProduktArt,
  app: App,
  tierart: TierartEnum,
  created: boolean,
  tiergruppe: TiergruppeState,
  read: ReadChildState,
): TgvProduktSnapshot[] => {
  const animals = tiergruppe.tiere.filter(tier => tier.tierart === tierart && tier.umfang.thh);
  if (!created || animals.length === 0) {
    return [];
  }
  const child = read(app) as ChildProdukt | undefined;
  return [
    {
      key: app,
      art,
      app,
      tierNamen: animals.map(animal => displayName(animal, tiergruppe.tiere.indexOf(animal))),
      tierart,
      produktGeladen: hasProduktlinien(child),
      linien: linienOf(child),
      zahlweise: zahlweiseOf(child),
    },
  ];
};

export const buildProduktSnapshots = (
  tiergruppe: TiergruppeState,
  read: ReadChildState,
): TgvProduktSnapshot[] => {
  const tkv = tiergruppe.tiere
    .filter(
      tier =>
        tier.umfang.tkv &&
        tier.tierart !== null &&
        Object.hasOwn(tiergruppe.angebotsIds, tier.cardId),
    )
    .map((tier): TgvProduktSnapshot => {
      const angebotsId = tiergruppe.angebotsIds[tier.cardId];
      const child = read(App.TKV_V2, angebotsId) as ChildProdukt;
      return {
        key: tier.cardId,
        art: TgvProduktArt.TKV,
        app: App.TKV_V2,
        angebotsId,
        tierNamen: [displayName(tier, tiergruppe.tiere.indexOf(tier))],
        tierart: tier.tierart,
        produktGeladen: hasProduktlinien(child),
        linien: linienOf(child),
        zahlweise: zahlweiseOf(child),
      };
    });
  return [
    ...tkv,
    ...thhSnapshot(
      TgvProduktArt.THH_HUND,
      App.THH_HUND,
      TierartEnum.HUND,
      tiergruppe.thhAngelegt.thhHund,
      tiergruppe,
      read,
    ),
    ...thhSnapshot(
      TgvProduktArt.THH_PFERD,
      App.THH_PFERD,
      TierartEnum.PFERD,
      tiergruppe.thhAngelegt.thhPferd,
      tiergruppe,
      read,
    ),
  ];
};
